import type { FC, ReactNode } from 'react'

import { Image as ImageIcon, Paperclip } from 'lucide-react'
import { cn } from '@/lib/utils'
import { StatusLabel } from '@/components/generals/status-label'
import { BuckyMascot } from '@/components/generals/bucky-mascot'
import { ConceptScreen } from '@/components/landing/concept-screen'
import { TabletFrame } from '@/components/landing/tablet-frame'

import { concept } from '@/constants/content'

const Marker: FC<{ number: number, className?: string }> = ({ number, className }): ReactNode => (
    <span
        aria-hidden
        className={cn(
            'z-10 grid size-7 place-items-center rounded-full bg-soft-white font-heading text-xs font-bold text-navy shadow-[0_0_0_4px_rgb(255_138_61/0.45)]',
            className
        )}
    >
        {number}
    </span>
)

// A labelled illustration of the intended experience: game, top-centre subtitles, Bucky in the corner,
// a sample customer question and a product asset card. Built from the existing Play-to-Pitch tablet.
export const ConceptExperience: FC = (): ReactNode => {
    const { experience } = concept

    return (
        <div id='bucky-experience' className='mt-24 sm:mt-32'>
            <div className='reveal max-w-2xl'>
                <h3 className='text-[1.75rem] leading-tight font-bold tracking-tight text-soft-white sm:text-4xl'>
                    {experience.heading}
                </h3>

                <p className='mt-3 text-lg leading-relaxed text-cool-grey-light'>
                    {experience.intro}
                </p>
            </div>

            <div className='mt-10 grid gap-10 lg:grid-cols-[1.3fr_0.7fr] lg:items-center lg:gap-12'>
                <figure className='reveal relative lg:px-6 lg:py-10'>
                    <div className='relative'>
                        <TabletFrame>
                            <ConceptScreen stage='engage' language='en' score={140} speaking />
                        </TabletFrame>

                        <Marker number={1} className='absolute top-[52%] left-[30%]' />
                        <Marker number={2} className='absolute top-[4%] left-1/2 -translate-x-1/2' />
                        <Marker number={3} className='absolute right-[13%] bottom-[22%]' />
                    </div>

                    <div className='mt-5 grid gap-3 sm:grid-cols-2 lg:mt-0 lg:block'>
                        <div className='relative flex items-start gap-3 rounded-xl bg-navy-900/95 p-3 shadow-[0_20px_40px_-20px_rgb(0_0_0/0.9)] ring-1 ring-soft-white/15 lg:absolute lg:bottom-0 lg:-left-2 lg:w-64'>
                            <Marker number={4} className='absolute -top-3 -left-3' />

                            <span className='block size-8 shrink-0 rounded-full bg-navy-800 p-0.5 ring-2 ring-orange/70'>
                                <BuckyMascot size={32} decorative className='size-full' />
                            </span>

                            <div className='flex flex-col gap-1.5 text-xs leading-snug'>
                                <p className='self-end rounded-lg rounded-tr-sm bg-orange px-2.5 py-1.5 font-medium text-navy'>
                                    {experience.sampleQuestion}
                                </p>

                                <p className='rounded-lg rounded-tl-sm bg-navy-700 px-2.5 py-1.5 text-soft-white'>
                                    {experience.sampleAnswer}
                                </p>
                            </div>
                        </div>

                        <div className='relative flex items-center gap-3 rounded-xl bg-navy-900/95 p-3 shadow-[0_20px_40px_-20px_rgb(0_0_0/0.9)] ring-1 ring-soft-white/15 lg:absolute lg:-top-6 lg:-right-2 lg:w-56'>
                            <Marker number={5} className='absolute -top-3 -left-3' />

                            <span className='grid size-12 shrink-0 place-items-center rounded-lg bg-[linear-gradient(135deg,#31527A,#1B3350)] text-orange'>
                                <ImageIcon className='size-5' aria-hidden />
                            </span>

                            <div className='leading-tight'>
                                <p className='text-sm font-semibold text-soft-white'>
                                    {experience.assetTitle}
                                </p>

                                <p className='mt-1 inline-flex items-center gap-1 text-[11px] text-cool-grey-light'>
                                    <Paperclip className='size-3' aria-hidden />
                                    {experience.assetType}
                                </p>
                            </div>
                        </div>
                    </div>

                    <figcaption className='mt-5 flex justify-center lg:mt-2'>
                        <StatusLabel status='concept' />
                    </figcaption>
                </figure>

                <ol className='reveal flex flex-col gap-3'>
                    {experience.callouts.map((callout, index) => (
                        <li key={callout.title} className='flex gap-4 rounded-xl border border-soft-white/10 bg-navy-900/50 p-4'>
                            <span className='grid size-7 shrink-0 place-items-center rounded-full bg-soft-white font-heading text-xs font-bold text-navy'>
                                {index + 1}
                            </span>

                            <div>
                                <h4 className='font-heading text-base font-bold text-soft-white'>
                                    {callout.title}
                                </h4>

                                <p className='mt-1 text-sm leading-relaxed text-cool-grey-light'>
                                    {callout.text}
                                </p>
                            </div>
                        </li>
                    ))}
                </ol>
            </div>
        </div>
    )
}
